import { useCallback, useContext, useMemo, useReducer } from "react";
import { fetchQuery, graphql, useFragment } from "react-relay";
import type { GroupBase, OnChangeValue, Options, OptionsOrGroups } from "react-select";

import { compact } from "lodash";

import { AppContext } from "~/common";
import { type Contact, ContactsQuery } from "~/common/EmailListInput";
import { isValidEmail } from "~/lib/utils";
import { environment } from "~/models";
import type { SelectorOption } from "~/ui/selectors/AsyncCreatableSelector";

import type { EmailListInputContactsQuery } from "~/__generated__/EmailListInputContactsQuery.graphql";
import type { ResponseStatusChoices } from "~/__generated__/MeetingsPanelAttendanceMutation.graphql";
import type { useParticipantsOptions_viewer$key } from "~/__generated__/useParticipantsOptions_viewer.graphql";

const fragment = graphql`
	fragment useParticipantsOptions_viewer on Viewer {
		account {
			isPersonalType
			emailDomainList
		}
	}
`;

export type Participant = Contact & {
	isOptional?: boolean;
	responseStatus?: ResponseStatusChoices;
};

type ParticipantSelectorOption = SelectorOption & Participant;

export type ParticipantsReducerAction = {
	type: "ADD_PARTICIPANT" | "REMOVE_PARTICIPANT" | "SET_OPTIONAL";
	participant: Participant;
	isOptional?: boolean;
};

const participantsReducer = (prevState: Participant[], action: ParticipantsReducerAction) => {
	switch (action.type) {
		case "ADD_PARTICIPANT": {
			if (prevState.find(p => p.id === action.participant.id)) return prevState;
			return [action.participant, ...prevState];
		}
		case "REMOVE_PARTICIPANT": {
			return prevState.filter(({ id }) => id !== action.participant.id);
		}
		case "SET_OPTIONAL": {
			return prevState.map(p => {
				if (p.id !== action.participant.id) return p;

				return {
					...p,
					isOptional: Boolean(action.isOptional),
				};
			});
		}
		default: {
			return prevState;
		}
	}
};

export function useParticipantsState(initialParticipants: Participant[] = []) {
	return useReducer(participantsReducer, initialParticipants);
}

/**
 * A hook for setting up an AsyncCreatableSelector for selecting participants.
 *  This hook is tightly coupled with AsyncCreatableSelector, sorry.
 */
export default function useParticipantsOptions(
	viewerKey: useParticipantsOptions_viewer$key,
	selectedParticipants: Participant[],
) {
	const loadOptions = useCallback(
		async (searchString: string) => {
			const result = await fetchQuery<EmailListInputContactsQuery>(environment, ContactsQuery, {
				search: searchString.trim(),
				first: 10,
			}).toPromise();

			if (!result?.viewer.contacts) return [];
			const { contacts, account } = result.viewer;

			let options: OnChangeValue<ParticipantSelectorOption, true> = compact(
				contacts.edges.map(edge => (edge && edge.node ? edge.node : null)),
			).map(node => ({
				value: node.id,
				label: node.fullName,
				id: node.id,
				email: node.email,
				fullName: node.fullName,
				image: node.image,
				hasConfiguredUser: node.user?.isConfigured ?? false,
				isGuest: node.user?.isGuest, // user the contact user isGuest if available,
				isExternal:
					!node.user?.isGuest &&
					!account.isPersonalType && // otherwise check if the email domains match ignoring case
					!account.emailDomainList.some(domain => node?.email.toLowerCase().endsWith(`@${domain.toLowerCase()}`)),
				user: node.user,
			}));

			// Excluding previously added participants
			options = options.filter(({ email }) => !selectedParticipants.find(participant => email === participant.email));

			return options;
		},
		[selectedParticipants],
	);

	const organizerEmail = useContext(AppContext).user?.email;
	const { account } = useFragment(fragment, viewerKey);
	const validDomains = useMemo(() => new Array(...account.emailDomainList), [account.emailDomainList]);
	const isPersonalWorkspace = useMemo(() => account.isPersonalType, [account.isPersonalType]);

	const makeNewOptionEmail = useCallback(
		(value: string): string =>
			!value.includes("@") && !isPersonalWorkspace
				? `${value.toLowerCase()}@${validDomains[0] || "gmail.com"}`
				: value.toLowerCase(),
		[validDomains, isPersonalWorkspace],
	);

	/**
	 * Matches AsyncCreatableSelector's getNewOptionData prop
	 */
	const getNewOptionData = useCallback(
		(value: string): ParticipantSelectorOption => {
			const email = makeNewOptionEmail(value).trim();
			const isEmailFromDomain = validDomains.some(domain => email.toLowerCase().endsWith(`@${domain.toLowerCase()}`));
			const isExternal = isValidEmail(email) && !isEmailFromDomain && !isPersonalWorkspace;

			return {
				value: value,
				label: email,
				id: value,
				email,
				fullName: value,
				isExternal,
			};
		},
		[makeNewOptionEmail, validDomains, isPersonalWorkspace],
	);

	/**
	 * Matches AsyncCreatableSelector's isValidNewOption prop
	 */
	const isValidNewOption = useCallback(
		(
			inputValue: string,
			value: OnChangeValue<ParticipantSelectorOption, true>,
			options: OptionsOrGroups<ParticipantSelectorOption, GroupBase<ParticipantSelectorOption>>,
		): boolean => {
			const trimmedInput = inputValue.trim();
			const email = makeNewOptionEmail(trimmedInput);
			return (
				isValidEmail(email) &&
				trimmedInput.length > 0 &&
				email !== organizerEmail &&
				!selectedParticipants.find(participant => participant.email === email) &&
				!(options as Options<ParticipantSelectorOption>).find(option => option.email.toLowerCase() === email)
			);
		},
		[makeNewOptionEmail, selectedParticipants, organizerEmail],
	);

	return {
		loadOptions,
		getNewOptionData,
		isValidNewOption,
	};
}
